import { motion } from "framer-motion";

// ─── Editorial palette ────────────────────────────────────────────────────
export const WINE     = "#7B1416";
export const WINE_L   = "#A42022";
export const CREAM    = "#F5EFE4";
export const CREAM2   = "#EDE4D4";
export const CHARCOAL = "#1C1917";
export const SAND     = "#C4A882";
export const SAND_L   = "#D4B89A";
export const FONT_DISPLAY = "'Montserrat', Georgia, serif";
export const FONT_SANS = "'Montserrat', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

/**
 * Warm cream background with grain texture, dot grid, and scattered decorative blobs.
 * Use on light/cream sections.
 */
export function CreamBackground({ children, variant = "default" }: { children: React.ReactNode; variant?: "default" | "deep" }) {
  const bg = variant === "deep" ? CREAM2 : CREAM;
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: bg, fontFamily: FONT_SANS }}>
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: GRAIN_SVG, backgroundSize: "200px 200px" }} />
      {/* Dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage: `radial-gradient(circle, ${CHARCOAL}0A 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
        }}
      />
      {/* Decorative scattered blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full opacity-40" style={{ background: `radial-gradient(circle, ${WINE}10 0%, transparent 65%)` }} />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[28rem] w-[28rem] rounded-full opacity-30" style={{ background: `radial-gradient(circle, ${SAND}18 0%, transparent 65%)` }} />
      {/* Top ruled band */}
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${WINE}, ${SAND}, ${WINE})` }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

/**
 * Dark charcoal background with warm glows — for dramatic sections.
 */
export function DarkBackground({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden" style={{ background: CHARCOAL, fontFamily: FONT_SANS }}>
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: GRAIN_SVG, backgroundSize: "200px 200px" }} />
      {/* Warm radial glows */}
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 40% at 30% 20%, ${WINE}18 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 70% 80%, ${SAND}10 0%, transparent 50%)` }} />
      {/* Dot grid */}
      <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: `radial-gradient(circle, ${SAND}14 1px, transparent 1px)`, backgroundSize: "32px 32px" }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

/**
 * Wine-red poster background — for CTA sections.
 */
export function WineBackground({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden" style={{ background: WINE, fontFamily: FONT_SANS }}>
      {/* Grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: GRAIN_SVG, backgroundSize: "200px 200px" }} />
      {/* Sand radial glow */}
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 50% 40% at 50% 30%, ${SAND}15 0%, transparent 50%)` }} />
      {/* Top + bottom ruled bands */}
      <div className="absolute top-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="absolute bottom-0 left-0 right-0 h-2" style={{ background: SAND }} />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

/**
 * Editorial section label — "EDITION" style with ruled lines.
 */
export function SectionLabel({ label, dark }: { label: string; dark?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px w-8" style={{ background: dark ? "rgba(255,255,255,0.25)" : WINE }} />
      <span className="text-[10px] font-bold uppercase tracking-[0.3em]" style={{ color: dark ? "rgba(255,255,255,0.55)" : WINE, fontFamily: FONT_SANS }}>
        {label}
      </span>
      <div className="h-px w-8" style={{ background: dark ? "rgba(255,255,255,0.25)" : WINE }} />
    </div>
  );
}

/**
 * Reveal-on-scroll wrapper.
 */
export function Reveal({ children, delay = 0, className }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/**
 * Editorial display headline — charcoal + wine italic split.
 */
export function DisplayHeadline({ top, bottom, dark }: { top: string; bottom: string; dark?: boolean }) {
  const topColor = dark ? "rgba(255,255,255,0.95)" : CHARCOAL;
  const bottomColor = dark ? SAND : WINE;
  return (
    <div className="mt-8 max-w-3xl">
      <Reveal delay={0.1}>
        <h2 style={{ fontFamily: FONT_DISPLAY, color: topColor, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5.5rem)", fontWeight: 700 }}>
          {top}
        </h2>
        <p style={{ fontFamily: FONT_DISPLAY, color: bottomColor, lineHeight: 0.92, letterSpacing: "-0.03em", fontSize: "clamp(2.5rem, 7vw, 5.5rem)", fontWeight: 700, fontStyle: "italic" }}>
          {bottom}
        </p>
      </Reveal>
    </div>
  );
}

/**
 * Sand corner decoration — L-shaped bracket with dot.
 */
export function SandCorner({ position, color = SAND }: { position: "tl" | "tr" | "bl" | "br"; color?: string }) {
  const pos = {
    tl: { top: -1, left: -1, rotate: "0deg" as const },
    tr: { top: -1, right: -1, rotate: "90deg" as const },
    bl: { bottom: -1, left: -1, rotate: "-90deg" as const },
    br: { bottom: -1, right: -1, rotate: "180deg" as const },
  }[position];
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" className="absolute" style={pos}>
      <path d="M 2 2 L 2 28 M 2 2 L 28 2" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <circle cx="2" cy="2" r="2.5" fill={color} />
    </svg>
  );
}
